import { useState, useMemo, useCallback, useContext, useEffect } from 'react';
import { CopyButton } from '@Components/CopyButton';

import * as Dfns from 'date-fns/fp';
import * as uuid from 'uuid';

import { MdBuild, MdKeyboardTab, MdInfoOutline } from 'react-icons/md';

import { Form, Row, Tooltip, Col, Alert, InputGroup, OverlayTrigger } from 'react-bootstrap';

import { Button } from '../../Components/Button';
import { Checkbox } from '../../Components/FormCheck';
import { IconButton } from '../../Components/IconButton';

import { useNetworkMap } from '../../redux/networks';
import { useSetError, useSetAreYouSure } from '../../redux/modals';

import { useLocationCompany } from '../../utils/hooks/useLocationCompany';
import { useExperimentFlag } from '../../utils/experiment-context';

import { DatePicker } from '../../Components/DatePicker';
import { InfoTooltip } from '../../Components/InfoTooltip';
import { SegmentedControlButton } from '../../Components/SegmentedControlButton';
import { Appearance, Design, Intent, Placement, Size } from '../../Components/designConstants';
import { Dropdown } from '../../Components/Dropdown';

import {
  OrderViewContext,
  subFlightsToPropertyArray,
  toPrettyDate,
  toPrettySpendWithCents
} from './order-view-utils';
import { PRINCIPAL_PRICING_FLAG } from './streaming-buying-constants';
import { Flight, FlightChange, StreamingNetworkInfo, SubFlight } from './streaming-buying-types';

import { MoveFlightModal } from './MoveFlightModal';
import { normalizePunctuation } from './streaming-buying-utils';

import './OrderView.scss';

const DATE_FORMAT = 'yyyy-MM-dd';
const CANCELED_DATE_FORMAT = 'EE, M/d/yy @ h:mma';

const DEFAULT_IMPRESSIONS_WINDOW = 30;

const regex = /[<>,]/; // regex to check for illegal characters in description or additional field

const TODAY = Dfns.format(DATE_FORMAT, new Date());

const DEFAULT_AUDIO_BUY_TYPE_OPTIONS = [
  'Streaming Audio - DAI Impression',
  'Podcast - DAI Impression',
  'Podcast - DAI Episodic',
  'Podcast - Embedded'
];

const DEFAULT_RTB_AUDIO_BUY_TYPE_OPTIONS = [
  'Streaming Audio - DAI Impression',
  'Podcast - DAI Impression'
];

const SPOT_POSITION_OPTIONS = [
  'Pre-Roll',
  'Mid-Roll',
  'Pre/Mid-Roll',
  'Post-Roll',
  'Pre/Mid/Post-Roll'
];

const IMPRESSIONS_WINDOW_OPTIONS = [DEFAULT_IMPRESSIONS_WINDOW, 42, 60, 90].map(days => ({
  value: String(days),
  label: `${days} days`
}));

type NumericInput = number | string | undefined;

const calculateImpressions = (spend: NumericInput, cpm: NumericInput): number => {
  if (spend !== '' && cpm !== '' && spend != null && cpm != null) {
    const newSpend = parseFloat(String(spend));
    const newCPM = parseFloat(String(cpm));
    if (newCPM !== 0) {
      return Math.round((newSpend / newCPM) * 1000);
    }
  }
  return 0;
};

const calculatePodcastSpend = (spotRate: NumericInput, numOfSpots: NumericInput): number => {
  if (spotRate !== '' && numOfSpots !== '' && spotRate != null && numOfSpots != null) {
    const newSpotRate = parseFloat(String(spotRate));
    const newNumOfSpots = parseFloat(String(numOfSpots));
    return newSpotRate * newNumOfSpots;
  }
  return 0;
};

const parseNumber = (num: unknown): number =>
  parseFloat(String(num ?? '').replace(/[^\d.]/g, '') || '0');

interface FlightFormProps {
  selectedFlight: string | number | undefined;
}

export const FlightForm = ({ selectedFlight }: FlightFormProps) => {
  const company = useLocationCompany();
  const setAreYouSure = useSetAreYouSure();
  const setError = useSetError();

  const {
    flightChangeMap,
    networkBuyingOptions,
    order,
    network,
    networkInfo,
    isMultiProperty,
    setHasPendingFormChanges,
    saveFlightChange,
    closeFlightForm,
    refetchOrder,
    showTitleOptions
  } = useContext(OrderViewContext);

  const canSeePrincipalPricing = useExperimentFlag(PRINCIPAL_PRICING_FLAG);

  const enableStreamingBuyingUpdates =
    networkInfo?.enableTaxonomyUpdates || company === 'blisspoint';

  const networkMap = useNetworkMap();
  const requiresDescriptions = networkMap?.[network]?.requiresDescriptions;

  const shouldHaveImpressionsWindowOptions =
    network?.includes('VOX') || network?.includes('BARSTOOL');

  const [flight, isNew, isReallyNew] = useMemo<[Partial<Flight>, boolean, boolean]>(() => {
    const { flights } = order;
    let foundFlight: Flight | FlightChange | undefined = flights.find(f => f.id === selectedFlight);
    if (!foundFlight) {
      foundFlight = Object.values(flightChangeMap)
        .filter(c => c.isNew)
        .find(c => c.id === selectedFlight);
    }

    return [foundFlight ?? {}, !foundFlight || Boolean(foundFlight.isNew), !foundFlight];
  }, [selectedFlight, order, flightChangeMap]);

  const timePeriod = useMemo(() => {
    if (isNew) {
      return 'new';
    }
    if ((flight.endDate ?? '') < TODAY) {
      return 'past';
    }
    if ((flight.startDate ?? '') <= TODAY) {
      return 'current';
    }
    return 'future';
  }, [isNew, flight]);

  const [localFlightChangeMap, setLocalFlightChangeMap] = useState<FlightChange>({});

  // localFlightChangeMap has enableStreamingBuyingUpdates as a key by default so want to
  // check if change count is greater than 1
  const hasChanges = useMemo(
    () => Object.keys(localFlightChangeMap).length > 1,
    [localFlightChangeMap]
  );
  useEffect(() => {
    setHasPendingFormChanges(hasChanges);
  }, [hasChanges, setHasPendingFormChanges]);
  const [prorated, setProrated] = useState(true);

  // If the selected flight changes, we should reset our local state
  useEffect(() => {
    setLocalFlightChangeMap({});
    setProrated(true);
  }, [selectedFlight]);

  const changeFlight = useCallback(
    <K extends keyof Flight>(field: K, value: Flight[K] | string | number | boolean | unknown) => {
      if (field === 'impressions' || field === 'spend') {
        setProrated(false);
      }
      setLocalFlightChangeMap(existing => ({ ...existing, [field]: value as Flight[K] }));
    },
    [setLocalFlightChangeMap, setProrated]
  );

  const defaultContent = networkInfo?.defaultContent || '';

  const DEFAULT_NEW_FLIGHT = useMemo<FlightChange>(
    () => ({
      description: '',
      content: defaultContent,
      additional: '',
      platform: '',
      impressions: undefined,
      spend: undefined,
      cpm: networkBuyingOptions?.cpm,
      subFlights: [],
      audioBuyType: '',
      episodeAirDates: '',
      spotRate: '',
      numOfSpots: '',
      spotPosition: '',
      showTitle: '',
      isNewShowTitle: false,
      useDescription: false
    }),
    [networkBuyingOptions, defaultContent]
  );

  const mergedFlight = useMemo<FlightChange>(() => {
    const existingChangeObj =
      (selectedFlight != null ? flightChangeMap[selectedFlight] : undefined) || {};
    return {
      ...DEFAULT_NEW_FLIGHT,
      ...flight,
      ...existingChangeObj,
      ...localFlightChangeMap
    };
  }, [DEFAULT_NEW_FLIGHT, flight, flightChangeMap, localFlightChangeMap, selectedFlight]);

  const resolvedFlight = useMemo<FlightChange>(() => {
    if ((mergedFlight.platform ?? '').includes('RTB')) {
      return { ...mergedFlight, dsp: mergedFlight.dsp || '' };
    }
    return mergedFlight;
  }, [mergedFlight]);

  const {
    cpm,
    tinuiti_cpm,
    derivedNetwork,
    description,
    content,
    additional,
    dsp,
    endDate,
    impressions,
    platform,
    audioBuyType,
    spend,
    startDate,
    canceled,
    showTitle,
    spotPosition,
    episodeAirDates,
    numOfSpots,
    spotRate,
    overrides,
    subFlights,
    useDescription
  } = resolvedFlight;

  const [flightFormVersion, setFlightFormVersion] = useState(
    !useDescription ? 'standard' : 'historical'
  );
  useEffect(() => {
    // enableStreamingBuyingUpdates lets us know how to map to a derived id when saving the flight
    changeFlight('enableStreamingBuyingUpdates', enableStreamingBuyingUpdates && !useDescription);
  }, [changeFlight, enableStreamingBuyingUpdates, useDescription]);

  const impressionsWindow = useMemo(() => {
    if (mergedFlight.impressionsWindow) {
      return mergedFlight.impressionsWindow;
    }
    if (shouldHaveImpressionsWindowOptions && episodeAirDates && episodeAirDates.length > 0) {
      const episodeAirDatesArr = episodeAirDates.split(', ').filter(date => !date.includes('_C:'));
      if (episodeAirDatesArr.length === 0) {
        return DEFAULT_IMPRESSIONS_WINDOW;
      }
      const lastAirDate = new Date(episodeAirDatesArr[episodeAirDatesArr.length - 1]);
      const lastDate = new Date(endDate ?? '');

      return Math.ceil((lastDate.getTime() - lastAirDate.getTime()) / (1000 * 3600 * 24));
    }
    return DEFAULT_IMPRESSIONS_WINDOW;
  }, [
    endDate,
    episodeAirDates,
    mergedFlight.impressionsWindow,
    shouldHaveImpressionsWindowOptions
  ]);

  const isPodcast = useMemo(() => audioBuyType && audioBuyType.includes('Podcast'), [audioBuyType]);

  const descriptionOptions = useMemo(() => {
    if (!networkBuyingOptions) {
      return [];
    }
    const fromChanges = Object.values(flightChangeMap)
      .map(c => c.description)
      .filter((d): d is string => Boolean(d));
    return [...new Set([...fromChanges, ...(networkBuyingOptions.descriptions ?? [])])].filter(
      Boolean
    );
  }, [networkBuyingOptions, flightChangeMap]);

  const contentOptions = useMemo(() => {
    if (!networkBuyingOptions) {
      return [];
    }
    return networkBuyingOptions.content ?? [];
  }, [networkBuyingOptions]);

  const additionalOptions = useMemo(() => {
    if (!networkBuyingOptions) {
      return [];
    }
    const fromChanges = Object.values(flightChangeMap)
      .map(c => c.additional)
      .filter((a): a is string => Boolean(a));
    return [...new Set([...fromChanges, ...(networkBuyingOptions.additional ?? [])])].filter(
      Boolean
    );
  }, [networkBuyingOptions, flightChangeMap]);

  const platformOptions = useMemo(() => {
    if (!(networkMap && network)) {
      return [];
    }
    const ourPlatforms = [...(networkMap[network]?.platforms ?? [])];

    if (ourPlatforms.some(p => /^Audio.*$/.test(p))) {
      ourPlatforms.push('Audio Bonus');
    }
    if (ourPlatforms.some(p => /^Audio RTB.*$/.test(p))) {
      ourPlatforms.push('Audio RTB Bonus');
    }
    if (ourPlatforms.some(p => /^Streaming.*$/.test(p))) {
      ourPlatforms.push('Streaming Bonus');
    }
    if (ourPlatforms.some(p => /^Streaming RTB.*$/.test(p))) {
      ourPlatforms.push('Streaming RTB Bonus');
    }
    if (ourPlatforms.some(p => /^Display.*$/.test(p))) {
      ourPlatforms.push('Display Bonus');
    }
    return ourPlatforms;
  }, [networkMap, network]);

  const dspOptions = useMemo(() => {
    if (!networkBuyingOptions) {
      return [];
    }
    return networkBuyingOptions.dsps ?? [];
  }, [networkBuyingOptions]);

  const disabledBuyTypes = useMemo<Record<string, string[]>>(() => {
    if (!(networkMap && network)) {
      return {};
    }
    return (networkMap[network] as StreamingNetworkInfo).disabledBuyTypes ?? {};
  }, [network, networkMap]);

  const audioBuyTypeOptions = useMemo(() => {
    if (!(networkMap && network)) {
      return [];
    }

    const disabledAudioBuyTypes = disabledBuyTypes.Audio;
    return DEFAULT_AUDIO_BUY_TYPE_OPTIONS.filter(buyType => {
      if (!disabledAudioBuyTypes) {
        return true;
      }
      return !disabledAudioBuyTypes.includes(buyType);
    });
  }, [disabledBuyTypes.Audio, network, networkMap]);

  const rtbAudioBuyTypeOptions = useMemo(() => {
    if (!(networkMap && network)) {
      return [];
    }

    const disabledAudioBuyTypes = disabledBuyTypes['Audio RTB'];
    return DEFAULT_RTB_AUDIO_BUY_TYPE_OPTIONS.filter(buyType => {
      if (!disabledAudioBuyTypes) {
        return true;
      }

      return !disabledAudioBuyTypes.includes(buyType);
    });
  }, [disabledBuyTypes, network, networkMap]);

  const currentProperties = useMemo(() => subFlightsToPropertyArray(subFlights), [subFlights]);

  const propertyOptions = useMemo(() => {
    if (!networkMap) return [];
    const children = networkMap[network]?.children ?? [];
    return children
      .filter(shortCode => !currentProperties.includes(shortCode) && networkMap[shortCode]?.buyable)
      .map(child => ({
        label: `${networkMap[child]?.name} (${child})`,
        value: child
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [networkMap, network, currentProperties]);

  const isBonus = useMemo(() => (platform || '').includes('Bonus'), [platform]);

  const proratable = useMemo(
    () => !prorated && startDate && endDate && (isBonus ? impressions : spend),
    [prorated, startDate, endDate, isBonus, impressions, spend]
  );

  const prorate = useCallback(() => {
    if (!proratable) {
      return;
    }
    const numDays =
      Dfns.differenceInCalendarDays(Dfns.parseISO(startDate ?? ''), Dfns.parseISO(endDate ?? '')) +
      1;
    if (isBonus) {
      changeFlight('impressions', Math.round(((impressions ?? 0) * numDays) / 7));
    } else {
      const newSpend = Math.round((((spend ?? 0) * numDays) / 7) * 100) / 100;
      changeFlight('spend', newSpend);
      changeFlight('impressions', calculateImpressions(newSpend, cpm));
    }
    setProrated(true);
  }, [impressions, spend, cpm, startDate, endDate, isBonus, proratable, changeFlight]);

  const flightWithoutLocalChanges = useMemo(() => {
    const existingChangeObj =
      (selectedFlight != null ? flightChangeMap[selectedFlight] : undefined) || {};
    return {
      ...DEFAULT_NEW_FLIGHT,
      ...flight,
      ...existingChangeObj
    };
  }, [flightChangeMap, selectedFlight, flight, DEFAULT_NEW_FLIGHT]);

  const onCancel = useCallback(async () => {
    try {
      if (hasChanges) {
        await setAreYouSure({
          title: 'You have unsaved changes.',
          message:
            'You have unsaved changes. You are about to discard them. Do you want to continue?',
          cancelText: 'Never mind',
          okayText: "Yes, I'm sure"
        });
      }
      closeFlightForm();
    } catch {
      // noop. This is when set are you sure is a cancel
    }
  }, [closeFlightForm, setAreYouSure, hasChanges]);

  const [createAnother, setCreateAnother] = useState(false);

  const onSave = useCallback(async () => {
    try {
      if (
        timePeriod === 'current' &&
        localFlightChangeMap.startDate &&
        localFlightChangeMap.startDate !== flightWithoutLocalChanges.startDate
      ) {
        await setAreYouSure({
          title: 'Change the start date on a running flight?',
          message:
            "You're about to change the start date on a currently running flight. This is VERY dangerous and probably unnecessary. You should not do this unless you're ABSOLUTELY CERTAIN this will not cause any problems. Are you sure you want to change the start date on this flight?",
          cancelText: 'Never mind',
          okayText:
            "I understand the risks and I'm absolutely certain that I want to change the start date on this flight.",
          intent: Intent.ERROR
        });
      } else if (timePeriod === 'past') {
        await setAreYouSure({
          title: 'Change past flight?',
          message:
            "You're about to save changes to a flight that has already happened. This is VERY dangerous and there is almost no circumstance where you should do this. Are you sure you want to modify this already-ended flight?",
          cancelText: 'Never mind',
          okayText:
            "I understand the risks and I'm sure I want to modify this already-finished flight.",
          intent: Intent.ERROR
        });
      }
    } catch {
      return;
    }
    const changes: FlightChange = { ...localFlightChangeMap };
    const existingChanges: FlightChange =
      (selectedFlight != null ? flightChangeMap[selectedFlight] : undefined) || {};
    const fullNewFlight = {
      ...DEFAULT_NEW_FLIGHT,
      ...flight,
      ...existingChanges,
      ...localFlightChangeMap
    };

    if (!fullNewFlight.id) {
      changes.id = uuid.v4();
      changes.isNew = true;
    }
    if (!fullNewFlight.platform) {
      setError({
        title: 'No Platform',
        message: 'Platform is required.'
      });
      return;
    }
    if (fullNewFlight.platform.includes('Audio') && fullNewFlight.audioBuyType === '') {
      setError({
        title: 'No Audio Buy Type',
        message: 'Audio Buy Type is required.'
      });
      return;
    }
    if (fullNewFlight.startDate == null || fullNewFlight.endDate == null) {
      setError({
        title: 'No dates',
        message: 'A date range is required.'
      });
      return;
    }
    if (fullNewFlight.platform.includes('RTB') && !fullNewFlight.dsp) {
      setError({
        title: 'No DSP',
        message: 'An RTB flight must have a DSP.'
      });
      return;
    }
    if (isPodcast && !fullNewFlight.showTitle) {
      setError({
        title: 'No Show Title',
        message: 'Show Title is required.'
      });
      return;
    }
    if (isPodcast && !fullNewFlight.spotPosition) {
      setError({
        title: 'No Spot Position',
        message: 'Spot Position is required.'
      });
      return;
    }
    if (isBonus) {
      if (fullNewFlight.impressions == null) {
        setError({
          title: 'No impressions',
          message: 'For bonus flights, impressions are required.'
        });
        return;
      }
    } else {
      if (fullNewFlight.cpm == null) {
        setError({
          title: 'No CPM',
          message: 'For non-bonus flights, CPM is required.'
        });
        return;
      }
      if (
        (fullNewFlight.audioBuyType?.includes('DAI Episodic') ||
          fullNewFlight.audioBuyType?.includes('Embedded')) &&
        fullNewFlight.spotRate === ''
      ) {
        setError({
          title: 'No spot rate',
          message: 'For non-bonus DAI episodic or Embedded flights, spot rate is required.'
        });
        return;
      }
      if (
        (fullNewFlight.audioBuyType?.includes('DAI Episodic') ||
          fullNewFlight.audioBuyType?.includes('Embedded')) &&
        fullNewFlight.numOfSpots === ''
      ) {
        setError({
          title: 'No # of spots',
          message: 'For non-bonus DAI Episodic or Embedded flights, # of spots is required.'
        });
        return;
      }
      if (fullNewFlight.spend == null) {
        setError({
          title: 'No spend',
          message: 'For non-bonus flights, spend is required.'
        });
        return;
      }
    }

    for (const [key, name] of [
      ['spend', 'Spend'],
      ['cpm', 'CPM'],
      ['impressions', 'Impressions']
    ] as const) {
      const value = localFlightChangeMap[key];
      if (value != null) {
        const parsed = parseFloat(String(value));
        if ((value as unknown) === '' || Number.isNaN(parsed)) {
          setError({
            title: `Invalid ${name}`,
            message: `${name} has to be a valid number.`
          });
          return;
        }
        changes[key] = parsed;
      }
    }
    if (fullNewFlight.startDate > fullNewFlight.endDate) {
      setError({
        title: 'Invalid Date Range',
        message: 'Start date must occur before end date.'
      });
      return;
    }

    if (isMultiProperty && subFlightsToPropertyArray(fullNewFlight.subFlights).length === 0) {
      setError({
        title: 'No Properties',
        message: 'Multi-Property networks must have at least one property set.'
      });
      return;
    }

    const fullSpend = ({ ...fullNewFlight, ...changes }.spend as number) ?? 0;
    const lineItemMinimum = networkBuyingOptions?.lineItemMinimum ?? 0;
    if (!fullNewFlight.platform?.includes('RTB') && fullSpend < lineItemMinimum) {
      try {
        await setAreYouSure({
          title: 'Under Line Item Minimum',
          message: `Your spend (${toPrettySpendWithCents(
            fullSpend
          )}) is less than the line item minimum (${toPrettySpendWithCents(
            lineItemMinimum
          )}). Are you sure you want to save?`,
          okayText: 'Yes, save anyway',
          cancelText: 'Never mind'
        });
      } catch {
        return;
      }
    }

    // If one override is set but not the other
    const overridesObj = fullNewFlight.overrides || undefined;
    const spendOverrideMissing = !overridesObj || overridesObj.spend == null;
    const impsOverrideMissing = !overridesObj || overridesObj.impressions == null;
    if (spendOverrideMissing !== impsOverrideMissing) {
      setError({
        title: 'Incomplete Wrench',
        message: 'You must set both (or neither) spend and impressions when wrenching.'
      });
      return;
    }

    if (flightFormVersion === 'historical' && requiresDescriptions && !fullNewFlight.description) {
      setError({
        title: 'Description Required',
        message: 'This network requires all flights have descriptions.'
      });
      return;
    }

    if (
      fullNewFlight.description &&
      regex.test(fullNewFlight.description) &&
      !descriptionOptions.includes(fullNewFlight.description)
    ) {
      setError({
        title: 'Illegal characters used for new Description',
        message: 'Please replace the following if used:\n<\n>\n, (comma)'
      });
      return;
    }

    if (flightFormVersion === 'standard' && !fullNewFlight.content && !isPodcast) {
      setError({
        title: 'Content Required',
        message: 'Content must be selected.'
      });
      return;
    }

    const firstWordOfAdditional = fullNewFlight.additional?.trim().split(/\s+/)[0]?.toLowerCase();
    const contentLower = fullNewFlight.content?.toLowerCase();
    if (
      flightFormVersion === 'standard' &&
      !isPodcast &&
      fullNewFlight.additional &&
      fullNewFlight.content &&
      firstWordOfAdditional === contentLower
    ) {
      setError({
        title: 'Duplicate Info',
        message: `Please do not retype the content value into the additional field. 
                  If you are remaking an existing flight, please reach out to #media-booking-support for assistance. 
                  The description will need to be updated manually.`
      });
      return;
    }

    /*
     * Allow users to edit flights that were accidentally made in Standard Tab.
     * Prevent users from creating flights in Standard Tab if the description already exists.
     */
    if (
      flightFormVersion === 'standard' &&
      !isPodcast &&
      !fullNewFlight.derivedNetwork &&
      descriptionOptions.includes(
        (fullNewFlight.content ?? '').concat(
          fullNewFlight.additional ? ` - ${fullNewFlight.additional}` : ''
        )
      )
    ) {
      setError({
        title: 'This description already exists.',
        message: `Please set this flight up in the Historical Tab.
                  Look for '${(fullNewFlight.content ?? '').concat(
                    fullNewFlight.additional ? ` - ${fullNewFlight.additional}` : ''
                  )}' in the description dropdown.`
      });
      return;
    }

    if (fullNewFlight.additional && regex.test(fullNewFlight.additional)) {
      setError({
        title: 'Illegal characters used for Additional Details',
        message: 'Please replace the following if used:\n<\n>\n, (comma)'
      });
      return;
    }

    let newFlightChangeObj: FlightChange;
    if (isNew) {
      newFlightChangeObj = { ...fullNewFlight, ...changes };
    } else {
      newFlightChangeObj = { ...existingChanges };
      const keys = Object.keys({ ...existingChanges, ...changes }) as (keyof FlightChange)[];
      for (const key of keys) {
        const changeVal = (changes as Record<string, unknown>)[key];
        const existingVal = (existingChanges as Record<string, unknown>)[key];
        const flightVal = (flight as Record<string, unknown>)[key];
        if (key === 'dsp') {
          // If we made no RTB-related changes, don't modify the dsp.
          if (changes.platform == null && changes.dsp == null) {
            continue;
          }
          const resolvedPlatform = fullNewFlight.platform ?? '';
          // If everything resolves to and RTB flight...
          if (resolvedPlatform.includes('RTB')) {
            // If we have a new dsp value, use it. If not, but the previous changes had said to
            // delete the existing dsp (false), delete the key (meaning no change)
            if (changeVal) {
              newFlightChangeObj.dsp = changeVal as string | false;
            } else if (existingVal === false) {
              delete newFlightChangeObj.dsp;
            }
          } else if ((flight.platform ?? '').includes('RTB')) {
            // If the fully resolved flight is not RTB, but the original is, then either these new
            // changes or pre-existing changes set the platform to non-RTB. Thus, the dsp needs to
            // be deleted from the flight (which used to be RTB). NOTE: it shouldn't be possible to
            // change the platform of an already-saved flight, but better safe than sorry.
            newFlightChangeObj.dsp = false;
          } else {
            // Otherwise, just set the dsp to have no change.
            delete newFlightChangeObj.dsp;
          }
          continue;
        }
        // If this key has no local change, just move on
        if (changeVal == null) {
          continue;
        }

        if (key === 'canceled') {
          // If the flight was previously canceled, but the user uncanceled and re-canceled, we want
          // the old date to remain (essentially delete this change and pretend it never happened).
          // If our local change agrees with the flight, then we should have no change. Either that
          // means we're saying cancel and already canceled flight, or uncancel a flight that's not
          // canceled, in which case we should not be registering a change. If they don't agree,
          // then use whatever this latest change is.
          if (Boolean(changeVal) !== Boolean(flightVal)) {
            newFlightChangeObj.canceled = changeVal as boolean | string;
          } else {
            delete newFlightChangeObj.canceled;
          }
          continue;
        }

        // If our new value matches the flight value, just delete any change to that value, if
        // present. Otherwise, use our new value.
        if (changeVal === flightVal) {
          delete (newFlightChangeObj as Record<string, unknown>)[key];
        } else {
          (newFlightChangeObj as Record<string, unknown>)[key] = changeVal;
        }
      }
    }
    saveFlightChange(fullNewFlight.id ?? newFlightChangeObj.id, newFlightChangeObj, !createAnother);
  }, [
    localFlightChangeMap,
    flightChangeMap,
    selectedFlight,
    DEFAULT_NEW_FLIGHT,
    flight,
    isPodcast,
    isBonus,
    isMultiProperty,
    networkBuyingOptions,
    flightFormVersion,
    requiresDescriptions,
    descriptionOptions,
    isNew,
    saveFlightChange,
    createAnother,
    timePeriod,
    flightWithoutLocalChanges,
    setAreYouSure,
    setError
  ]);

  const removeProperty = useCallback(
    (shortCode: string) => {
      const newSubFlights: SubFlight[] = [];
      for (const subFlight of subFlights ?? []) {
        // If this sub flight is the one we're removing
        if (subFlight.shortCode === shortCode) {
          // Tell it to end if it's existing. If it has no id, it's new, which means we just won't
          // add it back into the new array.
          if (subFlight.id) {
            newSubFlights.push({
              ...subFlight,
              ending: true
            });
          }
        } else {
          newSubFlights.push(subFlight);
        }
      }
      changeFlight('subFlights', newSubFlights);
    },
    [changeFlight, subFlights]
  );

  const addProperty = useCallback(
    (shortCode: string) => {
      const newSubFlights: SubFlight[] = [];
      let found = false;
      for (const subFlight of subFlights ?? []) {
        // If there is already a sub flight set to end with the same short code, this action is
        // essentially just undoing that. So just remove that modification.
        if (subFlight.shortCode === shortCode && subFlight.ending) {
          found = true;
          const { ending: _ending, ...withoutEnding } = subFlight;
          newSubFlights.push(withoutEnding);
        } else {
          newSubFlights.push(subFlight);
        }
      }
      if (!found) {
        newSubFlights.push({
          shortCode
        });
      }
      changeFlight('subFlights', newSubFlights);
    },
    [changeFlight, subFlights]
  );

  const canceledLabel = useMemo(() => {
    if (!canceled) {
      return undefined;
    }
    return canceled === true
      ? 'Just now'
      : Dfns.format(CANCELED_DATE_FORMAT, Dfns.parseISO(String(canceled)));
  }, [canceled]);

  const cancelFlight = useCallback(async () => {
    if (timePeriod === 'future' && !canceled) {
      try {
        const resetValues =
          flight.audioBuyType?.includes('Episodic') || flight.audioBuyType?.includes('Embedded')
            ? 'spot rate, # of spots, spend, CPM, and impressions'
            : 'spend, CPM, and impressions';
        await setAreYouSure({
          title: 'Canceling Flight',
          message: `You're about to cancel this flight. This will set the ${resetValues} to 0. Are you sure you want to continue?`,
          okayText: 'Continue'
        });
      } catch {
        return;
      }
      changeFlight('spend', 0);
      changeFlight('cpm', 0);
      changeFlight('tinuiti_cpm', 0);
      changeFlight('impressions', 0);
      if (flight.audioBuyType?.includes('Episodic') || flight.audioBuyType?.includes('Embedded')) {
        changeFlight('spotRate', 0);
        changeFlight('numOfSpots', 0);
      }
    }
    changeFlight('canceled', !canceled);
  }, [timePeriod, setAreYouSure, changeFlight, canceled, flight]);

  const [showWrench, setShowWrench] = useState(false);

  const {
    wrenchSpend,
    wrenchImps,
    clearWrenching,
    changeWrenchSpend,
    changeWrenchImps,
    calculateWrenchSpend,
    calculateWrenchImps
  } = useMemo(() => {
    const overridesObj = overrides || {};
    const setWrenchSpend = (spendValue: number | string) =>
      changeFlight('overrides', { ...overridesObj, spend: spendValue });
    const setWrenchImps = (impressionsValue: number | string) =>
      changeFlight('overrides', { ...overridesObj, impressions: impressionsValue });
    return {
      wrenchSpend: overridesObj.spend == null ? '' : overridesObj.spend,
      wrenchImps: overridesObj.impressions == null ? '' : overridesObj.impressions,
      clearWrenching: () => changeFlight('overrides', false),
      changeWrenchSpend: setWrenchSpend,
      changeWrenchImps: setWrenchImps,
      calculateWrenchSpend: () =>
        setWrenchSpend(
          // Do CPM math, but instead of dividing by 1000, divide by 10, round, then divide by 100
          // again, to keep it at max 2 decimal places
          Math.round(((cpm ?? 0) * (parseNumber(overridesObj.impressions) || 0)) / 10) / 100
        ),
      calculateWrenchImps: () =>
        setWrenchImps(cpm ? Math.round((1000 * parseNumber(overridesObj.spend)) / cpm) : 0)
    };
  }, [changeFlight, overrides, cpm]);

  const [showMoveFlight, setShowMoveFlight] = useState(false);

  return (
    <div className='flight-form'>
      <div className='header'>
        <div className='title'>{isReallyNew ? 'New Flight' : 'Modify Flight'}</div>
        {enableStreamingBuyingUpdates && !flight.id && (
          <>
            <InfoTooltip>
              Please check the Historical Tab to reuse existing descriptions. We have removed the
              ability to add new descriptions in the Historical Tab, so you&apos;ll need to use the
              Standard Tab in those cases.
            </InfoTooltip>
            <SegmentedControlButton
              size={Size.LG}
              options={[
                { key: 'historical', label: 'Historical' },
                { key: 'standard', label: 'Standard' }
              ]}
              selectedOption={flightFormVersion}
              onChange={() => {
                if (flightFormVersion === 'historical') {
                  setFlightFormVersion('standard');
                  changeFlight('description', '');
                  changeFlight('useDescription', false);
                  changeFlight('showTitle', '');
                  changeFlight('enableStreamingBuyingUpdates', true);
                  changeFlight('content', defaultContent);
                } else {
                  setFlightFormVersion('historical');
                  changeFlight('useDescription', true);
                  changeFlight('content', '');
                  changeFlight('additional', '');
                  changeFlight('showTitle', '');
                  changeFlight('enableStreamingBuyingUpdates', false);
                }
              }}
            />
          </>
        )}
      </div>
      {timePeriod === 'past' && (
        <Alert variant='danger'>
          This flight has already ended. You should not make changes to it.
        </Alert>
      )}
      {timePeriod === 'current' && (
        <Alert variant='info'>
          This flight is currently running. Some fields shouldn&apos;t be changed mid-flight.
        </Alert>
      )}
      <Form>
        <Row>
          <Form.Group as={Col}>
            <Form.Label>Platform</Form.Label>
            <Dropdown
              size={Size.LG}
              searchable
              clearable
              disabled={!isNew}
              placeholder='Platform'
              onChange={selection => {
                changeFlight('platform', selection ?? '');
                changeFlight('numOfSpots', '');
                changeFlight('spotRate', '');
                changeFlight('spend', selection?.includes('Bonus') ? 0 : '');
                changeFlight(
                  'cpm',
                  selection?.includes('Bonus') ? 0 : (networkBuyingOptions?.cpm ?? '')
                );
                changeFlight('audioBuyType', '');
                changeFlight('description', '');
                if (selection?.includes('Streaming') && flightFormVersion === 'standard') {
                  changeFlight('content', defaultContent);
                } else {
                  changeFlight('content', '');
                }
                changeFlight('additional', '');
                changeFlight('spotPosition', '');
                changeFlight('episodeAirDates', '');
                changeFlight('dsp', '');
                changeFlight('showTitle', '');
                changeFlight('impressions', '');
                changeFlight('startDate', undefined);
                changeFlight('endDate', undefined);
                changeFlight('impressionsWindow', DEFAULT_IMPRESSIONS_WINDOW);
              }}
              options={platformOptions}
              value={platform || null}
            />
          </Form.Group>
          {(platform || '').includes('RTB') && (
            <Form.Group as={Col} xs={6}>
              <Form.Label>DSP</Form.Label>
              <Dropdown
                size={Size.LG}
                searchable
                clearable
                disabled={!isNew}
                placeholder='DSP'
                onChange={selection => {
                  changeFlight('dsp', selection ?? '');
                }}
                options={dspOptions}
                value={dsp || null}
              />
            </Form.Group>
          )}
        </Row>
        {platform && platform.includes('Audio') && (
          <Row>
            <Form.Group as={Col}>
              <Form.Label>Audio Buy Type</Form.Label>
              <Dropdown
                size={Size.LG}
                searchable
                clearable
                disabled={!isNew}
                placeholder='Audio Buy Type'
                onChange={selection => {
                  changeFlight('audioBuyType', selection ?? '');
                  if (platform.includes('Bonus')) {
                    if (selection?.includes('Episodic') || selection?.includes('Embedded')) {
                      changeFlight('numOfSpots', 0);
                      changeFlight('spotRate', 0);
                    } else {
                      changeFlight('numOfSpots', '');
                      changeFlight('spotRate', '');
                    }
                    changeFlight('spend', 0);
                    changeFlight('cpm', 0);
                  } else {
                    changeFlight('numOfSpots', '');
                    changeFlight('spotRate', '');
                    changeFlight('spend', '');
                    changeFlight('cpm', networkBuyingOptions?.cpm ?? '');
                  }
                  changeFlight('description', '');
                  if (selection === 'Streaming Audio - DAI Impression') {
                    changeFlight('content', defaultContent);
                  } else {
                    changeFlight('content', '');
                  }
                  changeFlight('additional', '');
                  changeFlight('spotPosition', '');
                  changeFlight('episodeAirDates', '');
                  changeFlight('showTitle', '');
                  changeFlight('impressions', '');
                  changeFlight('startDate', undefined);
                  changeFlight('endDate', undefined);
                  changeFlight('impressionsWindow', DEFAULT_IMPRESSIONS_WINDOW);
                }}
                options={
                  platform.includes('Audio RTB') ? rtbAudioBuyTypeOptions : audioBuyTypeOptions
                }
                value={audioBuyType || null}
              />
            </Form.Group>
          </Row>
        )}
        {platform &&
          audioBuyType &&
          (audioBuyType.includes('Episodic') || audioBuyType.includes('Embedded')) && (
            <Row>
              <Form.Group as={Col} className='form-date-picker'>
                <Form.Label>Episode Air Dates</Form.Label>
                <OverlayTrigger
                  placement='right-start'
                  overlay={
                    <Tooltip>
                      <p>
                        Impressions are tracked from the first episode air date through{' '}
                        {DEFAULT_IMPRESSIONS_WINDOW} days past the last episode air date with the
                        exception of Vox and Barstool, which have more options for the window
                        length.
                      </p>
                      <p>
                        Note: You can view and modify currently selected dates in the Dates section
                        of the Calendar.
                      </p>
                    </Tooltip>
                  }
                >
                  <MdInfoOutline className='info-outline' />
                </OverlayTrigger>
                <div className='multi-date-picker'>
                  <DatePicker
                    multiDate
                    value={(episodeAirDates ?? '')
                      .split(', ')
                      .filter(date => !date.includes('_C:'))}
                    onChange={selection => {
                      const date_arr: string[] = [];
                      let savedSelection: string[] = !isNew
                        ? (flight.episodeAirDates?.split(', ') ?? [])
                        : [];
                      selection.forEach(selectedDate => {
                        // remove cancelled date if it is selected
                        if (!isNew && savedSelection.includes('_C:'.concat(selectedDate))) {
                          savedSelection = savedSelection.filter(
                            date => !date.includes(selectedDate)
                          );
                        }
                        date_arr.push(selectedDate);
                      });

                      // add cancelled dates from savedSelection
                      const cancelledDates: string[] = [];
                      if (!isNew) {
                        savedSelection.forEach(date => {
                          if (date.includes('_C:')) {
                            cancelledDates.push(date.replace('_C:', ''));
                          } else if (!date_arr.includes(date)) {
                            cancelledDates.push(date);
                          }
                        });
                      }
                      date_arr.sort();

                      const newStartDate = date_arr[0];
                      // calculating value for endDate to be 30 days after last spot air date by default
                      let newEndDate: Date | undefined;
                      if (selection.length > 0) {
                        newEndDate = new Date(date_arr[date_arr.length - 1].replace(/-/g, '/'));
                        // impressionsWindow will be 30 by default but may be changed for VOX and BARSTOOL before picking episodeAirDates
                        newEndDate.setDate(newEndDate.getDate() + impressionsWindow);
                      }

                      // want to add cancelled dates in after setting startDate and calculating endDate
                      if (!isNew && cancelledDates.length) {
                        cancelledDates.forEach(date => {
                          date_arr.push(date);
                        });
                        date_arr.sort();

                        for (let i = 0; i < date_arr.length; i++) {
                          const date = date_arr[i];
                          if (cancelledDates.includes(date)) {
                            date_arr[i] = '_C:'.concat(date);
                          }
                        }
                      }

                      changeFlight('episodeAirDates', selection ? date_arr.join(', ') : '');
                      changeFlight('startDate', newStartDate);
                      changeFlight(
                        'endDate',
                        selection.length > 0 && newEndDate
                          ? Dfns.format(DATE_FORMAT, newEndDate)
                          : date_arr[0]
                      );
                    }}
                  />
                </div>
              </Form.Group>
              {shouldHaveImpressionsWindowOptions && (
                <Form.Group as={Col}>
                  <Form.Label>Impressions Window</Form.Label>
                  <Dropdown
                    size={Size.LG}
                    searchable
                    placeholder='Impressions Window'
                    onChange={selection => {
                      if (!selection) return;
                      const windowDays = Number(selection);
                      changeFlight('impressionsWindow', windowDays);
                      // only update endDate if there are valid episodeAirDates selected
                      const date_arr = (episodeAirDates ?? '')
                        .split(', ')
                        .filter(date => !date.includes('_C:'));
                      if (date_arr.length > 0) {
                        const lastAirDate = date_arr[date_arr.length - 1];
                        const newEndDate = new Date(lastAirDate.replace(/-/g, '/'));

                        newEndDate.setDate(newEndDate.getDate() + windowDays);
                        changeFlight('endDate', Dfns.format(DATE_FORMAT, newEndDate));
                      }
                    }}
                    options={IMPRESSIONS_WINDOW_OPTIONS}
                    value={impressionsWindow ? String(impressionsWindow) : null}
                  />
                </Form.Group>
              )}
            </Row>
          )}
        {platform &&
          (!platform.includes('Audio') ||
            audioBuyType?.includes('Impression') ||
            audioBuyType?.includes('Old Audio Buy')) && (
            <Row>
              <Form.Group as={Col} className='form-date-picker'>
                <Form.Label>Dates</Form.Label>
                <div>
                  <DatePicker
                    range={{ start: startDate ?? '', end: endDate ?? '' }}
                    isOutsideRange={() => false}
                    onChange={range => {
                      changeFlight('startDate', range.start);
                      changeFlight('endDate', range.end);
                    }}
                  />
                </div>
              </Form.Group>
            </Row>
          )}
        {isPodcast && (
          <Row>
            <Form.Group as={Col}>
              <Form.Label>Show Title</Form.Label>
              <Dropdown
                size={Size.LG}
                searchable
                creatable
                clearable
                disabled={!isNew}
                placeholder='Show Title'
                reactSelectProps={{ formatCreateLabel: value => `New show title: ${value}` }}
                onChange={async option => {
                  const trimmedOption = normalizePunctuation(option ?? '');
                  if (
                    trimmedOption &&
                    trimmedOption !== showTitle &&
                    !showTitleOptions.some(({ value }) => value === trimmedOption)
                  ) {
                    try {
                      await setAreYouSure({
                        title: 'Making New Show Title',
                        message:
                          'You are about to make a new show title that will be available for all clients. Make sure that it is not already listed in the drop-down menu.  Put any additional or client-specific information in the description field.',
                        okayText: 'Use new show title'
                      });
                      changeFlight('isNewShowTitle', true);
                    } catch {
                      changeFlight('isNewShowTitle', false);
                      return;
                    }
                  }

                  if (trimmedOption === '') {
                    changeFlight('isNewShowTitle', false);
                  }
                  changeFlight('showTitle', trimmedOption);
                }}
                options={[...showTitleOptions].sort((a, b) => a.label.localeCompare(b.label))}
                value={showTitle}
              />
            </Form.Group>
          </Row>
        )}
        {!enableStreamingBuyingUpdates &&
          flightFormVersion === 'historical' &&
          platform &&
          (!platform.includes('Audio') || audioBuyType) && (
            <Row>
              <Form.Group as={Col}>
                <Form.Label>Description</Form.Label>
                <Dropdown
                  size={Size.LG}
                  searchable
                  creatable
                  clearable
                  disabled={!isNew}
                  placeholder='Description'
                  reactSelectProps={{ formatCreateLabel: value => `New description: ${value}` }}
                  onChange={async option => {
                    const trimmedOption = normalizePunctuation(option ?? '');
                    if (
                      trimmedOption &&
                      trimmedOption !== description &&
                      !descriptionOptions.includes(trimmedOption)
                    ) {
                      try {
                        await setAreYouSure({
                          title: 'Making New Description',
                          message:
                            "You are about to make a new description. You must make sure you follow our best practices. If you're unsure if your new description is appropriate, please reach out to Justin or in the #ops_team slack channel.",
                          okayText: 'Use new description'
                        });
                      } catch {
                        return;
                      }
                    }

                    changeFlight('useDescription', true);
                    changeFlight('description', trimmedOption);
                  }}
                  options={descriptionOptions}
                  value={description}
                />
              </Form.Group>
            </Row>
          )}
        {enableStreamingBuyingUpdates &&
          flightFormVersion === 'historical' &&
          platform &&
          (!platform.includes('Audio') || audioBuyType) && (
            <Row>
              <Form.Group as={Col}>
                <Form.Label>Description</Form.Label>
                <Dropdown
                  size={Size.LG}
                  searchable
                  clearable
                  disabled={!isNew}
                  placeholder='Description'
                  onChange={selection => {
                    changeFlight('useDescription', true);
                    changeFlight('description', selection ?? '');
                  }}
                  options={descriptionOptions}
                  value={description || null}
                />
              </Form.Group>
            </Row>
          )}
        {flightFormVersion === 'standard' &&
          !isPodcast &&
          platform &&
          (!platform.includes('Audio') || audioBuyType) && (
            <Row>
              <Form.Group as={Col}>
                <Form.Label>Content</Form.Label>
                <Dropdown
                  size={Size.LG}
                  searchable
                  clearable
                  disabled={!isNew}
                  placeholder='Content'
                  onChange={selection => {
                    changeFlight('useDescription', false);
                    changeFlight('content', selection ?? '');
                  }}
                  options={contentOptions}
                  value={content || null}
                />
              </Form.Group>
            </Row>
          )}
        {flightFormVersion === 'standard' &&
          platform &&
          (!platform.includes('Audio') || audioBuyType) && (
            <Row>
              <Form.Group as={Col}>
                <Form.Label>Additional Details</Form.Label>
                <Dropdown
                  size={Size.LG}
                  searchable
                  creatable
                  clearable
                  disabled={!isNew}
                  placeholder='Additional Details'
                  reactSelectProps={{
                    formatCreateLabel: value => `New additional detail: ${value}`
                  }}
                  onChange={option => {
                    changeFlight('additional', normalizePunctuation(option ?? ''));
                  }}
                  options={additionalOptions}
                  value={additional}
                />
              </Form.Group>
            </Row>
          )}
        {isPodcast && (
          <Row>
            <Form.Group as={Col}>
              <Form.Label>Spot Position</Form.Label>
              <Dropdown
                size={Size.LG}
                searchable
                clearable
                disabled={!isNew}
                placeholder='Spot Position'
                onChange={selection => {
                  changeFlight('spotPosition', selection ?? '');
                }}
                options={SPOT_POSITION_OPTIONS}
                value={spotPosition || null}
              />
            </Form.Group>
          </Row>
        )}
        {derivedNetwork && (
          <Row>
            <Form.Group as={Col}>
              <Form.Label>Derived Network</Form.Label>
              <InputGroup>
                <Form.Control
                  readOnly
                  className='derived-network-input'
                  type='text'
                  defaultValue={derivedNetwork}
                />
                <CopyButton
                  text={`${company} ${derivedNetwork || ''}`}
                  aria-label='Copy derived network'
                />
              </InputGroup>
            </Form.Group>
          </Row>
        )}
        {platform && (audioBuyType?.includes('Episodic') || audioBuyType?.includes('Embedded')) && (
          <Row>
            <Form.Group as={Col} xs={9}>
              <Form.Label>Spot Rate</Form.Label>
              <Form.Control
                readOnly={isBonus}
                type='number'
                value={spotRate}
                onChange={e => {
                  changeFlight('spotRate', e.target.value);
                  if (audioBuyType?.includes('Episodic') || audioBuyType?.includes('Embedded')) {
                    const podcastSpend = calculatePodcastSpend(e.target.value, numOfSpots);
                    changeFlight('spend', podcastSpend);
                    changeFlight('impressions', calculateImpressions(podcastSpend, cpm));
                  }
                }}
                onBlur={e => {
                  changeFlight(
                    'spotRate',
                    parseFloat((e.target.value || '').replace(/[^\d.]/g, '') || '0')
                  );
                }}
              />
            </Form.Group>
            <Form.Group as={Col} xs={3}>
              <Form.Label># of Spots</Form.Label>
              <Form.Control
                readOnly={isBonus}
                type='number'
                value={numOfSpots}
                onChange={e => {
                  changeFlight('numOfSpots', e.target.value);
                  if (audioBuyType?.includes('Episodic') || audioBuyType?.includes('Embedded')) {
                    const podcastSpend = calculatePodcastSpend(spotRate, e.target.value);
                    changeFlight('spend', podcastSpend);
                    changeFlight('impressions', calculateImpressions(podcastSpend, cpm));
                  }
                }}
                onBlur={e => {
                  changeFlight(
                    'numOfSpots',
                    parseFloat((e.target.value || '').replace(/[^\d.]/g, '') || '0')
                  );
                }}
              />
            </Form.Group>
          </Row>
        )}
        {platform && (!platform.includes('Audio') || audioBuyType) && (
          <Row>
            <Form.Group as={Col} xs={9}>
              <Form.Label>Spend</Form.Label>
              <Form.Control
                readOnly={
                  isBonus ||
                  audioBuyType?.includes('Episodic') ||
                  audioBuyType?.includes('Embedded')
                }
                type='number'
                value={spend ?? ''}
                onChange={e => {
                  changeFlight('spend', e.target.value);
                  if (
                    !platform?.includes('Audio') ||
                    audioBuyType?.includes('Impression') ||
                    audioBuyType?.includes('Old Audio Buy')
                  ) {
                    changeFlight('impressions', calculateImpressions(e.target.value, cpm));
                  }
                }}
                onBlur={e => {
                  changeFlight(
                    'spend',
                    parseFloat((e.target.value || '').replace(/[^\d.]/g, '') || '0')
                  );
                }}
              />
            </Form.Group>
          </Row>
        )}
        {platform && (!platform.includes('Audio') || audioBuyType) && (
          <Row>
            <Form.Group as={Col} xs={3}>
              <Form.Label>CPM</Form.Label>
              <Form.Control
                readOnly={isBonus}
                type='number'
                value={cpm ?? ''}
                onChange={e => {
                  changeFlight('cpm', e.target.value);
                  changeFlight('impressions', calculateImpressions(spend, e.target.value));
                }}
                onBlur={e => {
                  changeFlight(
                    'cpm',
                    parseFloat((e.target.value || '').replace(/[^\d.]/g, '') || '0')
                  );
                }}
              />
            </Form.Group>
            {networkBuyingOptions.enable_principal_based_buying && canSeePrincipalPricing && (
              <Form.Group as={Col} xs={6}>
                <Form.Label>Tinuiti CPM (Principal-Based Buying)</Form.Label>
                <Form.Control
                  readOnly={isBonus}
                  type='number'
                  value={tinuiti_cpm}
                  onChange={e => {
                    changeFlight('tinuiti_cpm', e.target.value);
                  }}
                  onBlur={e => {
                    changeFlight(
                      'tinuiti_cpm',
                      parseFloat((e.target.value || '').replace(/[^\d.]/g, '') || '0')
                    );
                  }}
                />
              </Form.Group>
            )}
          </Row>
        )}
        {platform && (!platform.includes('Audio') || audioBuyType) && (
          <Row>
            <Form.Group as={Col}>
              <Form.Label>Impressions</Form.Label>
              <Form.Control
                // Impressions is the one field inverted from the rest: for non-bonus flights it
                // is derived from spend/CPM, so only bonus flights (where both are forced to 0)
                // type it directly.
                readOnly={!isBonus}
                type='number'
                value={impressions ?? ''}
                onChange={e => {
                  changeFlight('impressions', e.target.value);
                }}
                onBlur={e => {
                  changeFlight(
                    'impressions',
                    Math.round(parseFloat((e.target.value || '').replace(/[^\d.]/g, '') || '0'))
                  );
                }}
              />
            </Form.Group>
            {(audioBuyType?.includes('Impression') ||
              audioBuyType?.includes('Old Audio Buy') ||
              !platform?.includes('Audio')) && (
              <Form.Group as={Col} xs='auto'>
                <Form.Label>&nbsp;</Form.Label>
                <div className='button-row'>
                  <Button
                    size={Size.LG}
                    className='prorate'
                    disabled={!proratable}
                    onClick={prorate}
                    tooltip={{
                      text: "When you hit this button, it takes the Spend, divides by 7, then multiplies by the number of days in the flight's date range.",
                      placement: Placement.LEFT
                    }}
                  >
                    Prorate
                  </Button>
                  {cpm != null && (
                    <IconButton
                      design={Design.NEUTRAL}
                      appearance={overrides ? Appearance.FILLED : Appearance.OUTLINED}
                      onClick={() => setShowWrench(v => !v)}
                    >
                      <MdBuild />
                    </IconButton>
                  )}
                </div>
              </Form.Group>
            )}
          </Row>
        )}
        {showWrench &&
          (audioBuyType?.includes('Impression') ||
            audioBuyType?.includes('Old Audio Buy') ||
            !platform?.includes('Audio')) && (
            <div className='wrench-section'>
              <Row>
                <Form.Group as={Col}>
                  <Form.Label>PDF Spend</Form.Label>
                  <Form.Control
                    size='sm'
                    type='number'
                    value={wrenchSpend}
                    onBlur={() => changeWrenchSpend(parseNumber(wrenchSpend))}
                    onChange={e => {
                      changeWrenchSpend(e.target.value);
                    }}
                  />
                </Form.Group>
                <Form.Group as={Col} xs='auto'>
                  <Form.Label>&nbsp;</Form.Label>
                  <Button
                    className='w-100'
                    disabled={wrenchImps == null || wrenchImps === ''}
                    onClick={calculateWrenchSpend}
                  >
                    Calculate
                  </Button>
                </Form.Group>
              </Row>
              <Row>
                <Form.Group as={Col}>
                  <Form.Label>PDF Impressions</Form.Label>
                  <Form.Control
                    size='sm'
                    type='number'
                    value={wrenchImps}
                    onBlur={() => changeWrenchImps(parseNumber(wrenchImps))}
                    onChange={e => {
                      changeWrenchImps(e.target.value);
                    }}
                  />
                </Form.Group>
                <Form.Group as={Col} xs='auto'>
                  <Form.Label>&nbsp;</Form.Label>
                  <Button
                    className='w-100'
                    disabled={wrenchSpend == null || wrenchSpend === ''}
                    onClick={calculateWrenchImps}
                  >
                    Calculate
                  </Button>
                </Form.Group>
              </Row>
              <div className='hint'>
                When you press &quot;Calculate&quot;, it will fill that box using the CPM and the
                value in the <em>other</em> box.
              </div>
              <Row>
                <Form.Group as={Col}>
                  <Button
                    design={Intent.ERROR}
                    appearance={Appearance.OUTLINED}
                    onClick={clearWrenching}
                  >
                    Clear Overrides
                  </Button>
                </Form.Group>
              </Row>
            </div>
          )}
      </Form>
      {isMultiProperty && (
        <Row>
          <Form.Group as={Col}>
            <Form.Label>Current Properties</Form.Label>
            <div className={`property-list${timePeriod === 'past' ? ' past' : ''}`}>
              {currentProperties.length
                ? [...currentProperties].sort().map(property => (
                    <Button
                      size={Size.LG}
                      key={property}
                      appearance={Appearance.EMPTY}
                      className='property-chip'
                      disabled={timePeriod === 'past'}
                      onClick={() => removeProperty(property)}
                    >
                      {property}
                    </Button>
                  ))
                : 'None'}
            </div>
          </Form.Group>
          <Form.Group as={Col}>
            <Form.Label>Add Property</Form.Label>
            <Dropdown
              size={Size.LG}
              searchable
              placeholder='New Property'
              disabled={timePeriod === 'past'}
              value={null}
              onChange={selection => selection && addProperty(selection)}
              options={propertyOptions}
            />
          </Form.Group>
        </Row>
      )}
      {Boolean((subFlights || []).length) && (
        <div className='sub-flight-list'>
          <div className='sub-flight headers'>
            <div className='property'>Property</div>
            <div className='date'>Start</div>
            <div className='date'>End</div>
          </div>
          {[...(subFlights ?? [])]
            // TODO: play with this to make nulls and such sort nicely. I'm not sure at this
            // moment if I want them above or below and whether I want start/end dates
            // ascending/descending.
            .sort((a, b) => {
              const aStart = a.id ? a.startDate || '' : '3';
              const bStart = b.id ? b.startDate || '' : '3';
              if (aStart !== bStart) return bStart.localeCompare(aStart);
              const aEnd = a.endDate || '';
              const bEnd = b.endDate || '';
              if (aEnd !== bEnd) return bEnd.localeCompare(aEnd);
              return a.shortCode.localeCompare(b.shortCode);
            })
            .map(sub => {
              const isNewSubFlight = !sub.id;
              let startDisp;
              let endDisp;
              let current = true;
              let changed = false;
              if (isNewSubFlight) {
                changed = true;
                if (isNew) {
                  startDisp = <em>Same as flight</em>;
                } else {
                  startDisp = <strong>Today</strong>;
                }
                endDisp = <em>Same as flight</em>;
              } else {
                if (sub.startDate) {
                  startDisp = toPrettyDate(sub.startDate);
                  if (sub.startDate > TODAY) {
                    current = false;
                  }
                } else {
                  startDisp = <em>Same as flight</em>;
                }
                if (sub.ending && (sub.startDate ?? '') < TODAY) {
                  changed = true;
                  endDisp = <strong>YESTERDAY</strong>;
                } else if (sub.endDate) {
                  endDisp = toPrettyDate(sub.endDate);
                  if (sub.endDate < TODAY) {
                    current = false;
                  }
                } else {
                  endDisp = <em>Same as flight</em>;
                }
              }
              return (
                <div
                  key={sub.id || sub.shortCode}
                  className={`sub-flight${current ? '' : ' not-current'}`}
                >
                  <div className={`property${changed ? ' changed' : ''}`}>
                    {sub.shortCode} {sub.zeroLength && <small>(0 len)</small>}
                  </div>
                  <div className='date'>{startDisp}</div>
                  <div className='date'>{endDisp}</div>
                </div>
              );
            })}
        </div>
      )}
      <Form>
        <Row>
          <Form.Group as={Col} className='canceled-row'>
            <Button design={Intent.ERROR} onClick={cancelFlight}>
              {canceled ? 'Un-cancel' : 'Cancel Flight'}
            </Button>
            {canceled ? (
              <div className='label'>Canceled {canceledLabel}</div>
            ) : (
              <div className='label' />
            )}
            {!isNew && (
              <IconButton
                data-testid='move-flight-button'
                appearance={Appearance.OUTLINED}
                size={Size.MD}
                disabled={hasChanges}
                onClick={() => setShowMoveFlight(true)}
                tooltip={{ text: 'Move Flight', placement: Placement.LEFT }}
              >
                <MdKeyboardTab />
              </IconButton>
            )}
          </Form.Group>
        </Row>
      </Form>
      <div className='button-row'>
        <Button
          size={Size.LG}
          design={hasChanges ? Intent.ERROR : Design.NEUTRAL}
          onClick={onCancel}
        >
          {hasChanges ? 'Discard Changes' : 'Close'}
        </Button>
        <div className='save-container'>
          {isReallyNew && (
            <Checkbox
              className='create-another'
              // Though I hate using id attributes in React, the label on this checkbox doesn't work
              // properly without it
              id='createAnotherCheckbox'
              label='Create Another'
              checked={createAnother}
              onChange={e => setCreateAnother(e.target.checked)}
            />
          )}
          <Button size={Size.LG} disabled={!hasChanges} onClick={onSave}>
            Save
          </Button>
        </div>
      </div>
      {showMoveFlight && flight.id != null && (
        <MoveFlightModal
          flight={flight as Flight}
          onClose={() => setShowMoveFlight(false)}
          onSave={refetchOrder}
        />
      )}
    </div>
  );
};
